import { PetVaccine, VaccineStatus } from '@spike/medical-history-model';
import { FileWrapper } from '@spike/model';
import { VaccineApproval } from 'model/VaccineApproval';
import { v4 as uuid } from 'uuid';

export interface VaccineTableDisplayRow {
    key: string;
    vaccine: PetVaccine;
    pendingApproval?: VaccineApproval;
}

const normalizeName = (name?: string) => (name || '').trim().toLowerCase();

const getApprovalVaccines = (approval: VaccineApproval) => approval.vaccines || [];

export const matchesVaccineApproval = (vaccine: PetVaccine, approval: VaccineApproval): boolean => {
    const approvalVaccines = getApprovalVaccines(approval);
    const typeId = vaccine.type?.id;

    if (typeId !== undefined && typeId !== null) {
        if (
            String(typeId) === String(approval.vaccineId) ||
            approvalVaccines.some(item => String(item.id) === String(typeId))
        ) {
            return true;
        }
    }

    const vaccineName = normalizeName(vaccine.type?.name);
    return approvalVaccines.some(item => normalizeName(item.name) === vaccineName);
};

const buildRecordFromApproval = (approval: VaccineApproval): FileWrapper | undefined => {
    if (!approval.file?.url) {
        return undefined;
    }

    const fileName = approval.file.fileName || 'file';
    return {
        url: approval.file.url,
        fileName,
        file: { name: fileName } as File,
        remove: false
    };
};

const getPrimaryApprovalVaccine = (approval: VaccineApproval) => {
    const vaccines = getApprovalVaccines(approval);
    return vaccines.find(item => String(item.id) === String(approval.vaccineId)) || vaccines[0];
};

export const buildVaccineFromApproval = (approval: VaccineApproval): PetVaccine => {
    const primary = getPrimaryApprovalVaccine(approval);
    return {
        uuid: approval.uuid || uuid(),
        id: primary ? Number(primary.id) || undefined : undefined,
        expiration: primary?.expirationDate || approval.expirationDate,
        status: VaccineStatus.ACTIVE,
        type: {
            id: String(primary?.id || approval.vaccineId),
            name: primary?.name || approval.vaccineId
        },
        record: buildRecordFromApproval(approval)
    };
};

export const mergeVaccinesWithPendingApprovals = (
    vaccines: PetVaccine[],
    pendingApprovals: VaccineApproval[]
): VaccineTableDisplayRow[] => {
    const matchedApprovalIds = new Set<number>();
    const rows: VaccineTableDisplayRow[] = [];

    vaccines.forEach(vaccine => {
        const pendingApproval = pendingApprovals.find(
            approval => !matchedApprovalIds.has(approval.id) && matchesVaccineApproval(vaccine, approval)
        );

        if (pendingApproval) {
            matchedApprovalIds.add(pendingApproval.id);
            const primary = getPrimaryApprovalVaccine(pendingApproval);
            rows.push({
                key: `pending-${pendingApproval.id}`,
                vaccine: {
                    ...vaccine,
                    expiration: primary?.expirationDate || pendingApproval.expirationDate || vaccine.expiration,
                    type: vaccine.type || {
                        id: String(primary?.id || pendingApproval.vaccineId),
                        name: primary?.name || pendingApproval.vaccineId
                    },
                    record: buildRecordFromApproval(pendingApproval) ?? vaccine.record
                },
                pendingApproval
            });
            return;
        }

        rows.push({
            key: vaccine.uuid || `vaccine-${vaccine.id}`,
            vaccine
        });
    });

    pendingApprovals
        .filter(approval => !matchedApprovalIds.has(approval.id))
        .forEach(approval => {
            rows.push({
                key: `pending-${approval.id}`,
                vaccine: buildVaccineFromApproval(approval),
                pendingApproval: approval
            });
        });

    return rows;
};

export const canReviewVaccineApproval = (
    approval: VaccineApproval,
    canManageClients: boolean,
    marketplaceId?: number
): boolean => {
    if (!canManageClients) {
        return false;
    }

    if (!approval.locations || approval.locations.length === 0) {
        return true;
    }

    if (marketplaceId === undefined || marketplaceId === null) {
        return false;
    }

    return approval.locations.some(location => location.id === marketplaceId);
};
